// ABOUTME: Unified operating hours table — renders time inputs in single or range mode per column.
// ABOUTME: Used by DeliveryWindowForm (range mode) and OperationalHoursForm (single mode).
import {
  Icon,
  TableBase,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@hm/fundamental'
import {mdiInformationOutline} from '@mdi/js'
import React from 'react'

import {OperatingHoursTableProps} from '../types/operatingHoursTable'
import {CountryConfigsTooltip} from './CountryConfigsTooltip'
import {OverrideDot} from './OverrideDot'
import {TimePickerInput} from './TimePickerInput'

/** Renders a single time input cell — wraps in an error Tooltip when an error is present. */
const TimeCell: React.FC<{
  value: string
  error: string | undefined
  isDisabled: boolean
  dataCy: string
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
}> = ({value, error, isDisabled, dataCy, onChange}) => {
  const input = (
    <TimePickerInput
      variant="small"
      value={value}
      onChange={onChange}
      isDisabled={isDisabled}
      state={error ? 'error' : 'default'}
      data-cy={dataCy}
    />
  )

  if (error) {
    return (
      <CountryConfigsTooltip content={error} delayMs={300}>
        <div>{input}</div>
      </CountryConfigsTooltip>
    )
  }

  return input
}

export const OperatingHoursTable: React.FC<OperatingHoursTableProps> = ({
  columns,
  rows,
  onTimeChange,
  disabled = false,
  errors = {},
  firstColumnLabel,
  'data-cy': dataCy = 'operating-hours-table',
}) => {
  return (
    <TableBase className="table-fixed" data-cy={dataCy}>
      <TableHeader className="bg-neutral-100">
        <TableHeaderCell className="text-left text-sm font-semibold text-foreground p-2 w-[150px]">
          {firstColumnLabel ?? ''}
        </TableHeaderCell>
        {columns.map((col) => (
          <TableHeaderCell key={col.key} className="text-left text-sm font-semibold text-foreground p-2">
            <div className="flex items-center gap-0.5">
              <span>{col.label}</span>
              {col.tooltip && (
                <CountryConfigsTooltip content={col.tooltip} delayMs={300}>
                  <span className="text-foreground flex items-center cursor-default">
                    <Icon path={mdiInformationOutline} size="regular" />
                  </span>
                </CountryConfigsTooltip>
              )}
            </div>
          </TableHeaderCell>
        ))}
      </TableHeader>
      <TableBody>
        {rows.map((row, rowIndex) => (
          <TableRow key={row.key} className={rowIndex === rows.length - 1 ? '[&>td]:border-b-0' : ''}>
            <TableCell className="text-sm text-foreground p-2 font-medium">
              <div className="flex items-center gap-1">
                {row.isOverridden && <OverrideDot />}
                {row.label}
              </div>
            </TableCell>
            {columns.map((col) => {
              const mode = col.mode ?? 'range'

              if (mode === 'single') {
                const cellValue = row.values[col.key]
                const singleValue = typeof cellValue === 'string' ? cellValue : ''
                return (
                  <TableCell key={col.key} className="p-2">
                    <div className="w-full">
                      <TimeCell
                        value={singleValue}
                        error={errors[`${row.key}.${col.key}`]}
                        isDisabled={disabled}
                        dataCy={`${dataCy}-${row.key}-${col.key}`}
                        onChange={(e) => onTimeChange(row.key, col.key, e.target.value)}
                      />
                    </div>
                  </TableCell>
                )
              }

              // range mode (default)
              const cellValue = row.values[col.key]
              const range = typeof cellValue === 'object' ? cellValue : {start: '', end: ''}

              return (
                <TableCell key={col.key} className="p-2">
                  {row.disabledColumns?.[col.key] ? (
                    <span className="text-sm text-foreground_faded">{row.disabledColumns[col.key]}</span>
                  ) : (
                    <div className="flex items-center gap-1">
                      <div className="w-full">
                        <TimeCell
                          value={range.start}
                          error={errors[`${row.key}.${col.key}.start`]}
                          isDisabled={disabled}
                          dataCy={`${dataCy}-${row.key}-${col.key}-start`}
                          onChange={(e) => onTimeChange(row.key, col.key, e.target.value, 'start')}
                        />
                      </div>
                      <span className="text-foreground_faded">–</span>
                      <div className="w-full">
                        <TimeCell
                          value={range.end}
                          error={errors[`${row.key}.${col.key}.end`]}
                          isDisabled={disabled}
                          dataCy={`${dataCy}-${row.key}-${col.key}-end`}
                          onChange={(e) => onTimeChange(row.key, col.key, e.target.value, 'end')}
                        />
                      </div>
                    </div>
                  )}
                </TableCell>
              )
            })}
          </TableRow>
        ))}
      </TableBody>
    </TableBase>
  )
}
